<template>
  <div
    class="kanban-card"
    :class="{
      'kanban-card--dragging': isDragging,
      [`kanban-card--priority-${priority}`]: true,
    }"
    draggable="true"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @click="$emit('click', conversation)"
  >
    <!-- Header: avatar + contact name + unread badge -->
    <div class="kanban-card__header">
      <div class="kanban-card__avatar">
        <img
          v-if="avatarUrl"
          :src="avatarUrl"
          :alt="contactName"
          class="kanban-card__avatar-img"
        />
        <span v-else class="kanban-card__avatar-initials">
          {{ initials }}
        </span>
      </div>
      <div class="kanban-card__contact">
        <span class="kanban-card__name">{{ contactName }}</span>
        <span v-if="contactPhone" class="kanban-card__phone">{{ contactPhone }}</span>
      </div>
      <div v-if="unreadCount > 0" class="kanban-card__badge">
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </div>
    </div>

    <!-- Last message -->
    <div v-if="lastMessage" class="kanban-card__message">
      {{ lastMessage }}
    </div>

    <!-- Labels -->
    <div v-if="labelChips.length > 0" class="kanban-card__labels">
      <span
        v-for="chip in labelChips"
        :key="chip.title"
        class="kanban-card__label"
        :style="{ backgroundColor: chip.bgColor, color: chip.textColor }"
        :title="chip.title"
      >
        {{ chip.title }}
      </span>
    </div>

    <!-- Footer: priority + inbox + time -->
    <div class="kanban-card__footer">
      <span
        v-if="priorityConfig"
        class="kanban-card__priority"
        :style="{ color: priorityConfig.color }"
        :title="priorityConfig.label"
      >
        <i :class="[priorityConfig.icon, 'size-3']" />
      </span>
      <span class="kanban-card__inbox">
        <i class="i-lucide-inbox size-3 mr-0.5" />
        {{ inboxName }}
      </span>
      <span class="kanban-card__time">
        {{ timeAgo }}
      </span>
    </div>

    <!-- Assignee -->
    <div v-if="assigneeName" class="kanban-card__assignee">
      <i class="i-lucide-user size-3 mr-0.5" />
      {{ assigneeName }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useKanbanStore } from 'dashboard/stores/kanban';
import { useMapGetter } from 'dashboard/composables/store';

const PRIORITY_CONFIG = {
  urgent: { label: 'Urgente', color: '#EF4444', icon: 'i-lucide-flame' },
  high:   { label: 'Alta',    color: '#F97316', icon: 'i-lucide-arrow-up' },
  medium: { label: 'Média',   color: '#3B82F6', icon: 'i-lucide-minus' },
  low:    { label: 'Baixa',   color: '#6B7280', icon: 'i-lucide-arrow-down' },
};

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['click', 'drag-start', 'drag-end']);
const kanbanStore = useKanbanStore();

// All labels from store: [{ id, title, color, ... }]
const allLabels = useMapGetter('labels/getLabels');
// Inbox getter
const getInbox = useMapGetter('inboxes/getInbox');

// Map: title (lowercase) → label object
const labelsMap = computed(() => {
  const map = {};
  for (const label of (allLabels.value || [])) {
    map[label.title?.toLowerCase()] = label;
  }
  return map;
});

// Compute label chips with background/text colors for the conversation
const labelChips = computed(() => {
  const convLabels = props.conversation.labels || [];
  return convLabels.slice(0, 4).map(title => {
    const lowerTitle = title?.toLowerCase?.() || '';
    const labelData = labelsMap.value[lowerTitle];
    const hexColor = labelData?.color || '#6B7280';
    return {
      title,
      bgColor: hexColor + '22', // 13% opacity background
      textColor: hexColor,
    };
  });
});

const contactName = computed(
  () => props.conversation.meta?.sender?.name || 'Sem nome'
);
const contactPhone = computed(
  () => props.conversation.meta?.sender?.phone_number || ''
);
const avatarUrl = computed(
  () => props.conversation.meta?.sender?.thumbnail || ''
);
const inboxName = computed(() => {
  const inboxId = props.conversation.inbox_id;
  if (!inboxId) return '';
  const inbox = getInbox.value?.(inboxId);
  return inbox?.name || `#${inboxId}`;
});
const unreadCount = computed(() => props.conversation.unread_count || 0);
const assigneeName = computed(
  () => props.conversation.meta?.assignee?.name || ''
);
const priority = computed(
  () => props.conversation.priority || props.conversation.conversation_priority || 'none'
);
const priorityConfig = computed(() => PRIORITY_CONFIG[priority.value] || null);

const lastMessage = computed(() => {
  const content = props.conversation.last_non_activity_message?.content;
  if (!content) return '';
  return content.length > 80 ? content.substring(0, 80) + '...' : content;
});
const timeAgo = computed(() => {
  const ts = props.conversation.last_activity_at;
  if (!ts) return '';
  const diff = Math.floor((Date.now() / 1000 - ts) / 60);
  if (diff < 1) return 'agora';
  if (diff < 60) return `${diff}m`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h`;
  return `${Math.floor(diff / 1440)}d`;
});

const initials = computed(() => {
  const name = contactName.value;
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
});

const isDragging = computed(
  () => kanbanStore.dragState.draggingCardId === props.conversation.id
);

function onDragStart(event) {
  // Pass the conversationId and the SOURCE column id (not the native status):
  // columns are now identified by their backend id, and several columns can map
  // to the same native status.
  const fromColumnId =
    kanbanStore.getColumnForConversation(props.conversation)?.id ?? null;
  const payload = JSON.stringify({
    conversationId: props.conversation.id,
    fromStatus: fromColumnId,
  });
  event.dataTransfer.setData('text/plain', payload);
  event.dataTransfer.effectAllowed = 'move';
  kanbanStore.setDragCard(props.conversation.id);
  emit('drag-start', props.conversation);
}

function onDragEnd() {
  kanbanStore.clearDrag();
  emit('drag-end', props.conversation);
}
</script>

<style scoped>
.kanban-card {
  @apply bg-n-solid-2 rounded-lg p-3 mb-2 cursor-grab border border-n-weak shadow-sm
         hover:shadow-md transition-shadow duration-150 select-none;
  border-left: 3px solid transparent;
}
.kanban-card--dragging {
  @apply opacity-50;
}
.kanban-card--priority-urgent { border-left-color: #EF4444; }
.kanban-card--priority-high    { border-left-color: #F97316; }
.kanban-card--priority-medium  { border-left-color: #3B82F6; }
.kanban-card--priority-low     { border-left-color: #6B7280; }
.kanban-card--priority-none    { border-left-color: transparent; }

.kanban-card__header {
  @apply flex items-center gap-2 mb-2;
}
.kanban-card__avatar {
  @apply flex-shrink-0 w-7 h-7 rounded-full overflow-hidden flex items-center justify-center
         bg-n-slate-4 text-n-slate-11 text-xs font-medium;
}
.kanban-card__avatar-img {
  @apply w-full h-full object-cover;
}
.kanban-card__avatar-initials {
  @apply text-xs font-medium;
}
.kanban-card__contact {
  @apply flex-1 min-w-0;
}
.kanban-card__name {
  @apply block text-xs font-semibold text-n-slate-12 truncate;
}
.kanban-card__phone {
  @apply block text-xs text-n-slate-10 truncate;
}
.kanban-card__badge {
  @apply flex-shrink-0 bg-n-ruby-9 text-white text-xs font-bold rounded-full
         min-w-[18px] h-[18px] flex items-center justify-center px-1;
}
.kanban-card__message {
  @apply text-xs text-n-slate-10 mb-2 leading-relaxed;
}
.kanban-card__footer {
  @apply flex items-center gap-1.5 text-xs text-n-slate-9;
}
.kanban-card__priority {
  @apply flex-shrink-0 flex items-center;
}
.kanban-card__inbox {
  @apply flex items-center truncate flex-1 min-w-0;
}
.kanban-card__time {
  @apply flex-shrink-0 ml-auto;
}
.kanban-card__assignee {
  @apply flex items-center text-xs text-n-slate-9 mt-1.5;
}
.kanban-card__labels {
  @apply flex flex-wrap gap-1 mt-1.5 mb-0.5;
}
.kanban-card__label {
  @apply inline-flex items-center text-[10px] font-medium rounded px-1.5 py-0.5
         border border-transparent truncate max-w-[100px];
}
</style>
